import React, { memo, Suspense } from "react";
import ReactDOMServer from "react-dom/server";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import { appURLs } from "@/app/app-urls.mjs";

export type BadgeProps = {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconShape?:
    | React.ComponentType<React.SVGProps<SVGSVGElement>>
    | React.LazyExoticComponent<SVGComponent>;
  title: Translation | string;
  description?: Translation;
  flare?: "Bronze" | "Silver" | "Gold";
  hslOverride?: string;
  weight?: number;
  size?: "small" | "medium" | "large";
  style?: React.CSSProperties;
};
export type BadgeListProps = {
  list: BadgeProps[];
  max?: number;
  size?: "small" | "medium" | "large";
};

const FLARE_COLOR = {
  Gold: "39deg 86% 72%",
  Silver: "0deg 0% 75%",
  Bronze: "30deg 48% 58%",
};
const TYPOGRAPHY = {
  small: "label type-caption--semi",
  medium: "label type-callout--bold",
  large: "label type-title--bold",
};

function flareAsset(flare: BadgeProps["flare"]) {
  return `${appURLs.CDN}/blitz/ui/img/accolades/${flare}-Flare.webp`;
}
function ringAsset(flare: BadgeProps["flare"]) {
  return `${appURLs.CDN}/blitz/ui/img/accolades/${flare}-Ring.webp`;
}

const Badge = ({
  icon,
  iconShape,
  title,
  description,
  flare,
  hslOverride,
  size = "medium",
  style = {},
}: BadgeProps) => {
  const { t } = useTranslation();
  const Icon = iconShape || icon;

  const flareSize = 116;
  const hasFlare = size !== "small";
  const typography = TYPOGRAPHY[size];
  const titleRender = Array.isArray(title) ? t(...title) : title;
  const descriptionRender = Array.isArray(description)
    ? t(...description)
    : description;

  const tooltip =
    description &&
    ReactDOMServer.renderToStaticMarkup(
      <div
        style={{
          width: 200,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--sp-2)",
          textAlign: "center",
        }}
      >
        <p className="type-body2-form--active">{titleRender}</p>
        <p className="type-caption--semi color-shade1">{descriptionRender}</p>
      </div>,
    );

  return (
    <Container
      style={{
        "--color": hslOverride || FLARE_COLOR[flare || "Silver"],
        ...style,
      }}
      data-tip={tooltip}
      data-no-icon={!Icon}
      data-shape-only={!!iconShape}
      data-size={size}
    >
      {Icon && (
        <div className="icon">
          {iconShape && <div className="icon-shape-bg" />}
          <Suspense>
            <Icon />
          </Suspense>
          {hasFlare && (
            <>
              <img
                className="flare"
                src={flareAsset(flare)}
                loading="lazy"
                width={flareSize}
                height={flareSize}
              />
              <img
                className="flare"
                src={ringAsset(flare)}
                loading="lazy"
                width={flareSize}
                height={flareSize}
              />
            </>
          )}
        </div>
      )}
      <span className={typography}>{titleRender}</span>
    </Container>
  );
};

export default memo(Badge);

export const MoreBadges = styled("div")`
  & {
    position: relative;

    &:hover {
      .more {
        display: flex;
      }
    }
  }
  .more {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    gap: var(--sp-5);
    padding: var(--sp-4) 0;

    > * {
      box-shadow: 0 2px 15px 3px var(--shade9);
    }

    @container (inline-size <= 800px) {
      flex-wrap: wrap;
    }
  }
`;

export const BadgeList = styled("div")`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 1.5rem;
  row-gap: 1rem;
  padding-inline-start: 0.75rem;
  padding-block-start: 0.25rem;

  &.column {
    flex-direction: column;
    align-items: flex-end;
  }

  &[data-size="small"] {
    column-gap: 0.75rem;
    row-gap: 0.35rem;
    padding: 0;
  }
`;

const Container = styled("div")`
  --radii: var(--br);
  --svg-size: var(--sp-8_5);
  --height: 2rem;
  --padd: 0 0.75rem 0 2.125rem;
  --flare-scale: 0.9;
  position: relative;
  display: flex;
  align-items: center;
  color: hsla(var(--color) / 1);
  background: linear-gradient(
    208deg,
    hsla(var(--color) / 0.6),
    hsla(var(--color) / 0.2) 65%
  );
  text-shadow: 0 2px var(--shade10);

  /* medium size */
  height: var(--height);
  padding: var(--padd);
  border-radius: var(--radii);

  &[data-no-icon="true"] {
    --padd: 0 1rem;
  }

  &[data-shape-only="true"] {
    --svg-size: var(--sp-7);
    --flare-scale: 1;

    .flare {
      translate: -49.5% -51%;
    }
  }

  > * {
    position: relative;
    z-index: 2;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 1px;
    background: var(--shade9);
    border-radius: var(--radii);
    z-index: 1;
    pointer-events: none;
  }
  &::after {
    background: linear-gradient(
      29deg,
      hsla(var(--color) / 17%),
      hsla(var(--color) / 6%)
    );
  }

  img {
    max-width: none;
    pointer-events: none;
  }

  .icon {
    position: absolute;
    display: flex;
    top: 50%;
    left: 0;
    translate: -30% -50%;
    filter: drop-shadow(0px 0px 6px var(--shade10));
  }
  .icon-shape-bg {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: auto;
    aspect-ratio: 1;
    border-radius: 50%;
    background: linear-gradient(
      to bottom,
      hsl(var(--color)),
      hsla(var(--color) / 0.35),
      hsl(var(--color))
    );
    translate: -50% -50%;

    &::after {
      content: "";
      position: absolute;
      inset: 2px;
      background: color-mix(in hsl, var(--shade10), transparent 40%);
      border-radius: 50%;
    }
  }
  .flare {
    position: absolute;
    mix-blend-mode: screen;
    left: 50%;
    top: 50%;
    translate: -49.5% -50.5%;
    scale: var(--flare-scale);
  }
  svg {
    position: relative;
    width: var(--svg-size);
    height: var(--svg-size);
  }

  .label {
    max-width: 13ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &[data-size="small"] {
    --svg-size: var(--sp-7);
    --height: 1.75rem;
    --padd: 0 0.5rem 0 1.5rem;
    --radii: var(--br);

    &[data-no-icon="true"] {
      --padd: 0 0.5rem;
    }
    &[data-shape-only="true"] {
      --svg-size: var(--sp-4_5);
      --padd: 0 0.5rem 0 1.35rem;
    }
  }

  &[data-size="large"] {
    --svg-size: var(--sp-12);
    --height: 2.5rem;
    --padd: 0 1rem 0 3rem;

    &[data-shape-only="true"] {
      --svg-size: var(--sp-9);
      --flare-scale: 1.35;
    }
  }
`;
